import React, { useEffect, useState } from 'react';
import api from '../../services/api';

/**
 * "Rapid Options" tab of Manage Signals — admin-only watch feed (see OptionFlowService).
 * At 9:34 the at-the-money call + put of each watch-list stock (~1-month expiry) are picked
 * and followed every minute. A contract is flagged by the 9:35 / 9:39 opening read (OPENING —
 * the stock's predicted direction for the next 30 minutes), when it is up >= 12% vs 30 minutes
 * ago (MOMENTUM, from 10:15), or on an IV jump / unusual volume, and its REAL price 15 and 30 minutes later
 * and at the close is recorded — so the scorecard shows how the flags actually did.
 * Watch only — nothing here creates a signal or notifies users.
 */

const FLAG_STYLE = {
  OPENING: 'bg-orange-100 text-orange-800',
  MOMENTUM: 'bg-emerald-100 text-emerald-800',
  BIG_MOVE: 'bg-amber-100 text-amber-800',
  IV_JUMP: 'bg-fuchsia-100 text-fuchsia-800',
  UNUSUAL_VOL: 'bg-sky-100 text-sky-800',
};
const FLAG_LABEL = { OPENING: '🌅 Opening', MOMENTUM: '🚀 Momentum', BIG_MOVE: 'Big move', IV_JUMP: 'IV jump', UNUSUAL_VOL: 'Unusual vol' };

/** Times are stored in New York time — show the HH:MM part as-is. */
function etTime(s) {
  if (!s) return '';
  const m = String(s).match(/T(\d{2}):(\d{2})/);
  if (!m) return '';
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`;
}

function pct(v) {
  if (v == null) return '—';
  const n = Number(v);
  return `${n >= 0 ? '+' : ''}${n.toFixed(Math.abs(n) >= 100 ? 0 : 1)}%`;
}

function pctClass(v) {
  if (v == null) return 'text-slate-400';
  return Number(v) >= 0 ? 'text-emerald-600' : 'text-rose-600';
}

function money(v) {
  return v == null ? '—' : `$${Number(v).toFixed(2)}`;
}

/** Clickable column header: first click sorts biggest first, second click flips. */
export function SortTh({ label, k, sort, setSort, align = 'right', title }) {
  const active = sort.key === k;
  return (
    <th
      className={`px-3 py-2 font-medium cursor-pointer select-none whitespace-nowrap hover:text-slate-800 ${align === 'left' ? 'text-left' : 'text-right'} ${active ? 'text-slate-900' : ''}`}
      onClick={() => setSort(active ? { key: k, dir: -sort.dir } : { key: k, dir: -1 })}
      title={title || 'Sort'}
    >
      {label} {active ? (sort.dir < 0 ? '▼' : '▲') : <span className="text-slate-300">↕</span>}
    </th>
  );
}

const num = (v) => (v == null ? -1e9 : Number(v));
const SORT_VALUE = {
  firstAt: (r) => String(r.firstAt || ''),
  trigger: (r) => num(r.trigger30Pct),
  ret5: (r) => num(r.ret5Pct),
  ret10: (r) => num(r.ret10Pct),
  ret15: (r) => num(r.ret15Pct),
  ret30: (r) => num(r.ret30Pct),
  retClose: (r) => num(r.retClosePct ?? (r.firstMid > 0 ? (r.lastMid / r.firstMid - 1) * 100 : null)),
  volume: (r) => Number(r.volume || 0),
  ratio: (r) => (r.openInterest > 0 ? r.volume / r.openInterest : 0),
  iv: (r) => Number(r.iv || 0),
};

/** Wins / count / average for one outcome column (only rows that have it). */
function score(rows, key) {
  const vals = rows.map((r) => r[key]).filter((v) => v != null).map(Number);
  if (!vals.length) return null;
  const wins = vals.filter((v) => v > 0).length;
  return { n: vals.length, wins, avg: vals.reduce((a, b) => a + b, 0) / vals.length };
}

function ScoreCell({ label, s }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      {s ? (
        <div className="text-sm font-semibold text-slate-800">
          {s.wins}/{s.n} = {Math.round((s.wins / s.n) * 100)}%
          <span className={`ml-2 text-xs ${pctClass(s.avg)}`}>{pct(s.avg)} avg</span>
        </div>
      ) : (
        <div className="text-sm text-slate-400">—</div>
      )}
    </div>
  );
}

export default function RapidOptionsPanel() {
  const [enabled, setEnabled] = useState(null);
  const [enabledLoading, setEnabledLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [scanMsg, setScanMsg] = useState('');
  const [scanning, setScanning] = useState(false);
  const [view, setView] = useState('MOMENTUM');
  const [sort, setSort] = useState({ key: 'firstAt', dir: -1 }); // newest first
  const [showWatching, setShowWatching] = useState(false);

  const load = async () => {
    try {
      const r = await api.get('/signals/option-flow');
      setData(r.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not load Rapid Options');
    }
  };

  useEffect(() => {
    api.get('/signals/option-flow/enabled').then((r) => setEnabled(!!r.data?.enabled)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const toggle = async (checked) => {
    setEnabledLoading(true);
    try {
      const r = await api.patch(`/signals/option-flow/enabled?enabled=${checked}`);
      setEnabled(!!r.data?.enabled);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update setting');
    } finally {
      setEnabledLoading(false);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    setScanMsg('');
    try {
      const r = await api.post('/signals/option-flow/scan');
      setScanMsg(r.data.message || `Following ${r.data.tracked ?? 0} contracts — read ${r.data.contractsRead ?? 0}, ${r.data.created ?? 0} new flag(s)`);
      load();
    } catch (err) {
      setScanMsg(err.response?.data?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const all = data?.rows || [];
  const momentum = all.filter((r) => (r.flags || '').split(',').includes('MOMENTUM'));
  const opening = all.filter((r) => (r.flags || '').split(',').includes('OPENING'));
  const rows = all.filter((r) => {
    const flags = (r.flags || '').split(',');
    if (view === 'CALL' || view === 'PUT') return r.side === view;
    if (view === 'ALL') return true;
    return flags.includes(view);
  });
  const val = SORT_VALUE[sort.key];
  rows.sort((a, b) => {
    const x = val(a), y = val(b);
    return (x < y ? -1 : x > y ? 1 : 0) * sort.dir;
  });
  const watching = data?.watching || [];

  return (
    <div className="space-y-3">
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">Rapid Options — fast option moves (watch only)</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              At 9:34 the at-the-money call + put of {data?.symbols?.length ?? 15} liquid stocks are picked — the ~1-month contract ({data?.expiry || ''}) if it costs ≤${data?.premiumCap ?? 15},
              else the 1-2 week one ({data?.weekExpiry || ''}), else skipped — and followed every minute.
              🌅 Opening = next-30-minute direction read at 9:35 (stock gapped ≥0.5% then pulled back ≥0.3% → the gap side) and 9:39 (gapped and still going ≥0.3% with SPY → that way); call for up, put for down.
              🚀 Momentum = option up ≥{data?.momentumPct ?? 12}% (1-month) / ≥{data?.momentumPctWeek ?? 25}% (1-2 week) vs 30 minutes ago (10:15–2:30) · IV jump · Unusual vol.
              Each flag shows its real price 5, 10, 15 and 30 minutes later and at the close (filled in as time passes). Today only — cleared every morning.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-slate-700">
              <input
                type="checkbox"
                checked={!!enabled}
                disabled={enabled === null || enabledLoading}
                onChange={(e) => toggle(e.target.checked)}
              />
              Auto-scan
            </label>
            <button
              type="button"
              onClick={scanNow}
              disabled={scanning}
              className="text-xs bg-slate-900 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
              title="Runs one scan now (uses marketdata credits)"
            >
              {scanning ? 'Scanning…' : 'Scan now'}
            </button>
          </div>
        </div>
        {scanMsg && <p className="text-xs text-slate-500 mt-2">{scanMsg}</p>}

        {/* Today's scorecard for 🌅 Opening flags — bought at the flag price */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">🌅 Opening flags today</div>
            <div className="text-sm font-semibold text-slate-800">{opening.length}</div>
          </div>
          <ScoreCell label="Sold after 5 min" s={score(opening, 'ret5Pct')} />
          <ScoreCell label="Sold after 10 min" s={score(opening, 'ret10Pct')} />
          <ScoreCell label="Sold after 15 min" s={score(opening, 'ret15Pct')} />
          <ScoreCell label="Sold after 30 min" s={score(opening, 'ret30Pct')} />
          <ScoreCell label="Held to close" s={score(opening, 'retClosePct')} />
        </div>

        {/* Today's scorecard for 🚀 Momentum flags — bought at the flag price */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
          <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">🚀 Momentum flags today</div>
            <div className="text-sm font-semibold text-slate-800">{momentum.length}</div>
          </div>
          <ScoreCell label="Sold after 5 min" s={score(momentum, 'ret5Pct')} />
          <ScoreCell label="Sold after 10 min" s={score(momentum, 'ret10Pct')} />
          <ScoreCell label="Sold after 15 min" s={score(momentum, 'ret15Pct')} />
          <ScoreCell label="Sold after 30 min" s={score(momentum, 'ret30Pct')} />
          <ScoreCell label="Held to close" s={score(momentum, 'retClosePct')} />
        </div>

        <button type="button" onClick={() => setShowWatching((v) => !v)} className="text-[11px] text-slate-500 underline mt-2">
          {showWatching ? 'Hide' : 'Show'} the {watching.length} contracts being followed
        </button>
        {showWatching && (
          <div className="mt-2 flex flex-wrap gap-1">
            {watching.length === 0 && <span className="text-[11px] text-slate-400">None yet — contracts are picked at 9:34 ET while Auto-scan is on.</span>}
            {watching.map((w) => (
              <span key={`${w.symbol}-${w.side}`} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                {w.symbol} {w.side === 'CALL' ? 'C' : 'P'}{w.strike} {w.expiry ? w.expiry.slice(5) : ''} {money(w.mid)}
                {w.chg30Pct != null && <span className={`ml-1 ${pctClass(w.chg30Pct)}`}>{pct(w.chg30Pct)}/30m</span>}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1 bg-slate-100 rounded-xl p-1 w-fit">
        {[
          { id: 'OPENING', label: `🌅 Opening (${opening.length})` },
          { id: 'MOMENTUM', label: `🚀 Momentum (${momentum.length})` },
          { id: 'ALL', label: `All (${all.length})` },
          { id: 'CALL', label: 'Calls' },
          { id: 'PUT', label: 'Puts' },
          { id: 'IV_JUMP', label: 'IV jump' },
          { id: 'UNUSUAL_VOL', label: 'Unusual vol' },
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setView(f.id)}
            className={`text-xs px-3 py-1.5 rounded-lg transition ${view === f.id ? 'bg-slate-900 text-white font-semibold' : 'text-slate-600'}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {error && <div className="text-sm text-rose-600">{error}</div>}

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <SortTh label="Flagged" k="firstAt" sort={sort} setSort={setSort} align="left" />
              <th className="text-left px-3 py-2 font-medium">Contract</th>
              <SortTh label="30-min jump" k="trigger" sort={sort} setSort={setSort} title="Option change vs 30 minutes before the flag" />
              <th className="text-right px-3 py-2 font-medium">Price at flag</th>
              <SortTh label="+5 min" k="ret5" sort={sort} setSort={setSort} title="Real price 5 minutes after the flag" />
              <SortTh label="+10 min" k="ret10" sort={sort} setSort={setSort} title="Real price 10 minutes after the flag" />
              <SortTh label="+15 min" k="ret15" sort={sort} setSort={setSort} title="Real price 15 minutes after the flag" />
              <SortTh label="+30 min" k="ret30" sort={sort} setSort={setSort} title="Real price 30 minutes after the flag" />
              <SortTh label="Close / now" k="retClose" sort={sort} setSort={setSort} title="Price at the close (or latest, until 3:58)" />
              <th className="text-right px-3 py-2 font-medium">Stock</th>
              <SortTh label="Volume" k="volume" sort={sort} setSort={setSort} />
              <SortTh label="Vol/OI" k="ratio" sort={sort} setSort={setSort} title="Volume ÷ open interest" />
              <SortTh label="IV" k="iv" sort={sort} setSort={setSort} />
              <th className="text-left px-3 py-2 font-medium">Flags</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={14} className="px-3 py-6 text-center text-slate-400">
                  {enabled === false ? 'Auto-scan is off — turn it on or press Scan now.' : 'Nothing flagged yet today.'}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const ratio = r.openInterest > 0 ? r.volume / r.openInterest : null;
              const closeRet = r.retClosePct ?? (r.firstMid > 0 && r.lastMid != null ? (r.lastMid / r.firstMid - 1) * 100 : null);
              const closeMid = r.midClose ?? r.lastMid;
              return (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 whitespace-nowrap text-slate-600">{etTime(r.firstAt)}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="font-semibold text-slate-900">{r.symbol}</span>{' '}
                    <span className={r.side === 'CALL' ? 'text-emerald-700' : 'text-rose-700'}>{r.side}</span>{' '}
                    {r.strike}
                    <span className="text-slate-400"> · {r.expiry?.slice(5)}</span>
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap font-semibold ${pctClass(r.trigger30Pct)}`}>{pct(r.trigger30Pct)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">{money(r.firstMid)}</td>
                  {[['mid5', 'ret5Pct'], ['mid10', 'ret10Pct'], ['mid15', 'ret15Pct']].map(([m, p]) => (
                    <td key={m} className="px-3 py-2 text-right whitespace-nowrap">
                      {r[m] != null ? <>{money(r[m])} <span className={pctClass(r[p])}>{pct(r[p])}</span></> : <span className="text-slate-300">waiting</span>}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {r.mid30 != null ? <>{money(r.mid30)} <span className={`font-semibold ${pctClass(r.ret30Pct)}`}>{pct(r.ret30Pct)}</span></> : <span className="text-slate-300">waiting</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {money(closeMid)} <span className={pctClass(closeRet)}>{pct(closeRet)}</span>
                    <div className="text-[10px] text-slate-400">{r.midClose != null ? 'close' : etTime(r.lastAt)}</div>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {money(r.stockPrice)} <span className={pctClass(r.stockChgPct)}>{pct(r.stockChgPct)}</span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap text-slate-600">
                    {Number(r.volume || 0).toLocaleString()}
                    <div className="text-[10px] text-slate-400">OI {Number(r.openInterest || 0).toLocaleString()}</div>
                  </td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap ${ratio != null && ratio >= 3 ? 'font-semibold text-sky-700' : 'text-slate-600'}`}>
                    {ratio != null ? `${ratio.toFixed(1)}×` : '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap text-slate-600">{r.iv != null ? `${(r.iv * 100).toFixed(0)}%` : '—'}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(r.flags || '').split(',').filter(Boolean).map((f) => (
                        <span key={f} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${FLAG_STYLE[f] || 'bg-slate-100 text-slate-600'}`}>
                          {FLAG_LABEL[f] || f}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">
        Watch feed only. Backtest (5 days, ~1-month contracts, ≥12% in 30 min): 🚀 Momentum flags were up 30 minutes later about 68% of the time (+3.7% avg, before the bid/ask spread; time decay ~1.6%/day).
        The scorecard above shows today's real result.
      </p>
    </div>
  );
}
